import type * as Blockly from 'blockly/core';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import { registerHelperFunction, resolveBoard } from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import { registerOledInit } from '@/(routers)/boards/editor/block/generators/arduino/blocks/display/helper/oled_init';
import { generateJpFontCode } from '@/(routers)/boards/editor/block/generators/arduino/blocks/display/helper/oled_jp_font';

type Position = keyof (typeof BOARD_CONFIG)[keyof typeof BOARD_CONFIG]['pins'];

const SIZE_Y_OFFSETS: Record<string, number[]> = {
    '1': [0, 8, 16],
    '2': [0, 16],
    '3': [0],
};

const SIZE_JP_SCALE: Record<string, number> = {
    '1': 1,
    '2': 2,
    '3': 3,
};

let _oledUsed = false;
let _jpChars = '';

export function oledUsed(): boolean {
    return _oledUsed;
}

export function markOledUsed(): void {
    _oledUsed = true;
}

export function resetOled(): void {
    _oledUsed = false;
    _jpChars = '';
}

function collectJpChars(text: string): void {
    for (const ch of text) {
        const code = ch.codePointAt(0)!;
        if ((code >= 0x3041 && code <= 0x3096) || (code >= 0x30a1 && code <= 0x30f6)) {
            if (!_jpChars.includes(ch)) _jpChars += ch;
        }
    }
}

function hasJpChars(text: string): boolean {
    for (const ch of text) {
        const code = ch.codePointAt(0)!;
        if ((code >= 0x3041 && code <= 0x3096) || (code >= 0x30a1 && code <= 0x30f6)) return true;
    }
    return false;
}

const JP_DRAW_HELPER = `void _oledDrawJp(Adafruit_SSD1306& d, int16_t x, int16_t y, uint16_t code, uint8_t scale, uint16_t color) {
  for (uint8_t i = 0; i < _oledJpLkLen; i++) {
#ifdef ESP32
    if (_oledJpLk[i].c != code) continue;
    uint8_t idx = _oledJpLk[i].i;
    uint8_t base = idx * 4;
    uint16_t bo = _oledJpGlyphs[base] | ((uint16_t)_oledJpGlyphs[base+1] << 8);
    uint8_t w = _oledJpGlyphs[base + 2];
    uint8_t h = _oledJpGlyphs[base + 3];
#else
    if (pgm_read_word(&_oledJpLk[i].c) != code) continue;
    uint8_t idx = pgm_read_byte(&_oledJpLk[i].i);
    uint8_t base = idx * 4;
    uint16_t bo = pgm_read_byte(&_oledJpGlyphs[base]) | ((uint16_t)pgm_read_byte(&_oledJpGlyphs[base+1]) << 8);
    uint8_t w = pgm_read_byte(&_oledJpGlyphs[base + 2]);
    uint8_t h = pgm_read_byte(&_oledJpGlyphs[base + 3]);
#endif
    for (uint8_t yy = 0; yy < h; yy++) {
#ifdef ESP32
      uint8_t bits = _oledJpBmp[bo++];
#else
      uint8_t bits = pgm_read_byte(&_oledJpBmp[bo++]);
#endif
      for (uint8_t xx = 0; xx < w; xx++) {
        if (bits & (0x80 >> xx)) {
          if (scale == 1) {
            d.drawPixel(x + xx, y + yy, color);
          } else {
            d.fillRect(x + xx * scale, y + yy * scale, scale, scale, color);
          }
        }
      }
    }
    return;
  }
}

void _oledPrintJp(Adafruit_SSD1306& d, int16_t x, int16_t y, const char* str, uint8_t scale, uint16_t color) {
  int16_t cx = x;
  while (*str) {
    uint8_t b = (uint8_t)*str;
    if (b < 0x80) {
      d.setTextSize(scale);
      d.setTextColor(color);
      d.setCursor(cx, y);
      d.print((char)b);
      cx += 6 * scale;
      str++;
    } else {
      uint32_t code = 0;
      if ((b & 0xE0) == 0xC0) {
        if (str[1] == '\\0') { str++; continue; }
        code = (b & 0x1F) << 6 | ((uint8_t)str[1] & 0x3F);
        str += 2;
      } else if ((b & 0xF0) == 0xE0) {
        if (str[1] == '\\0' || str[2] == '\\0') { str++; continue; }
        code = (b & 0x0F) << 12 | ((uint8_t)str[1] & 0x3F) << 6 | ((uint8_t)str[2] & 0x3F);
        str += 3;
      } else { str++; continue; }
      _oledDrawJp(d, cx, y, (uint16_t)code, scale, color);
      cx += 8 * scale;
    }
  }
}`;

export function register(gen: any): void {
    gen.forBlock['oled_print'] = function (block: Blockly.Block): string {
        markOledUsed();

        const board = resolveBoard(block);
        const position = block.getFieldValue('POSITION') as Position;
        const size = block.getFieldValue('TEXT_SIZE') ?? '1';
        const bg = block.getFieldValue('BG_COLOR') ?? 'BLACK';
        const isWhiteBg = bg === 'WHITE';
        const textColor = isWhiteBg ? 'BLACK' : 'WHITE';
        const jpScale = SIZE_JP_SCALE[size] ?? 1;

        registerOledInit(board, position);

        const liveIds = block.inputList.filter((i) => i.name.startsWith('VAL_')).map((i) => i.name.replace('VAL_', ''));

        const yOffsets = SIZE_Y_OFFSETS[size] ?? [0];
        const lines: string[] = [];

        lines.push(`oled.clearDisplay();`);

        if (isWhiteBg) {
            lines.push(`oled.fillScreen(WHITE);`);
        }

        const rowValues = liveIds.map((id) => gen.valueToCode(block, `VAL_${id}`, 0) || '""');

        let hasJp = false;
        for (const val of rowValues) {
            const match = val.match(/^"(.*)"$/);
            if (match) {
                collectJpChars(match[1]);
                if (hasJpChars(match[1])) hasJp = true;
            }
        }

        if (hasJp) {
            registerHelperFunction('oledJpDraw', JP_DRAW_HELPER);
        }

        lines.push(`oled.setTextSize(${size});`);
        lines.push(`oled.setTextColor(${textColor});`);

        const types = (block as any)._types as Record<string, 'number' | 'string'>;
        liveIds.forEach((id, i) => {
            const value = rowValues[i];
            const y = yOffsets[i] ?? yOffsets[yOffsets.length - 1];
            const rowType = types[id] ?? 'number';
            const strContent = value.startsWith('"') && value.endsWith('"') ? value.slice(1, -1) : '';

            if (rowType === 'string' && hasJpChars(strContent)) {
                lines.push(`_oledPrintJp(oled, 0, ${y}, ${value}, ${jpScale}, ${textColor});`);
            } else {
                lines.push(`oled.setCursor(0, ${y});`);
                lines.push(`oled.print(${value});`);
            }
        });

        lines.push(`oled.display();`);

        return lines.join('\n') + '\n';
    };
}

export function getOledJpFontCode(): string {
    if (_jpChars.length === 0) return '';
    return generateJpFontCode(_jpChars);
}
